import type { Doc } from "building-types/doc";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import { docGetActiveOptionTemplateOverrides } from "doc/docGetOptionTemplates";
import { partialDocToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { mergeOptionTemplatesIntoDefinitions } from "doc/options/mergeOptionTemplatesIntoOptionSets";
import { evaluateOptionRuleEffects, ruleEffectsToChoices } from "doc/options/relations/evaluateOptionRuleEffects";

/**
 * This function applies option rules to a set of option choices where the user selected choices
 * (activeChoices) have already been applied (previousChoices). These rules are evaluated
 * based on the updated option choice state (previousChoices), but the effects of these rules
 * should not be applied until all rules have been resolved in order to prevent cascading changes.
 * @param doc The doc used for option rules / effects
 * @param previousChoices The state of option choices *after* the new choices have already been applied
 * @param activeChoices Optionally include only the choices being applied by the user. This is helpful
 * when we only want to apply rules that contain option references to the new choices.
 * @param skipOptionTemplateMerge If true, assumes doc.optionSets already has templates merged (optimization)
 * @returns A partial of optionChoices to be applied to the doc
 */
export function applyRulesToOptionChoices(
  doc: Pick<
    Doc,
    "optionSets" | "instanceOptionSets" | "optionEffects" | "optionRules" | "optionTemplates" | "optionSetOverrides"
  >,
  previousChoices: OptionChoices,
  activeChoices?: OptionChoices,
  skipOptionTemplateMerge: boolean = false,
): OptionChoices {
  const { optionEffects, optionRules } = doc;

  const definitions: OptionDefinitions = skipOptionTemplateMerge
    ? partialDocToOptionDefinitions(doc)
    : mergeOptionTemplatesIntoDefinitions(partialDocToOptionDefinitions(doc), [
        docGetActiveOptionTemplateOverrides(doc),
      ]);

  // The active choices may have already been combined with the previous choices, but there are
  // cases where they could have been removed so we'll combine them here to be safe
  const combinedChoices = { ...previousChoices, ...activeChoices };

  // Evaluate all rules against combined choices.
  const { active } = evaluateOptionRuleEffects(
    { ...definitions, optionRules, optionEffects },
    combinedChoices,
    activeChoices,
  );

  // Convert to choices and merge
  const ruleChoices = ruleEffectsToChoices(active);
  return { ...combinedChoices, ...ruleChoices };
}
